/**
 * Download utilities for artifacts and documents
 */ /**
 * Download text/code content as a file
 * Exports as markdown (.md) for text, .js for code, etc.
 */ export function downloadText(content) {
    const { title, content: text, kind } = content;
    // Handle presentations separately
    if (kind === 'presentation') {
        return downloadPresentation({
            title,
            content: text
        });
    }
    // Determine file extension based on kind
    let extension = 'md'; // default
    if (kind === 'code') {
        extension = 'js'; // could be enhanced to detect language
    } else if (kind === 'text') {
        extension = 'md';
    }
    const blob = new Blob([
        text
    ], {
        type: 'text/plain;charset=utf-8'
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${(title || 'document').replace(/\s+/g, '-')}.${extension}`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
}
/**
 * Extract theme from presentation HTML
 */ function extractThemeFromHtml(html) {
    const match = html.match(/data-theme=["']([^"']+)["']/);
    const theme = match?.[1];
    if (theme === 'federal-navy' || theme === 'modern-purple') {
        return theme;
    }
    return 'federal-navy'; // default
}
/**
 * Download presentation as PowerPoint file
 */ export async function downloadPresentation(content) {
    const { title, content: htmlContent } = content;
    // Lazy import to avoid bundling pptxgenjs unless needed
    const { convertToPptx, downloadPptx, parseHTMLToSlides } = await import('../lib/artifacts/presentation/editor/PresentationEditorWithControls');
    try {
        const slides = parseHTMLToSlides(htmlContent);
        const theme = extractThemeFromHtml(htmlContent);
        const blob = await convertToPptx({
            title,
            slides,
            theme
        });
        downloadPptx(blob, title);
    } catch (error) {
        console.error('Failed to export presentation:', error);
        // Fallback to HTML download
        const blob = new Blob([
            htmlContent
        ], {
            type: 'text/html;charset=utf-8'
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${(title || 'presentation').replace(/\s+/g, '-')}.html`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
    }
}
/**
 * Copy content to clipboard
 * @returns true if copy was successful, false otherwise
 */ export async function copyToClipboard(text) {
    // Try modern Clipboard API first
    if (navigator.clipboard?.writeText) {
        try {
            await navigator.clipboard.writeText(text);
            return true;
        } catch  {
        // Permission denied or other error, try fallback
        }
    }
    // Fallback for older browsers or if Clipboard API fails
    try {
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.opacity = '0';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        const success = document.execCommand('copy');
        document.body.removeChild(textArea);
        return success;
    } catch  {
        return false;
    }
}
